import Backbone from 'backbone'
import _ from 'lodash'
import template from './threshold-panel.hbs'
import selects from 'scoreboard/components/selects'
import icon from 'scoreboard/components/icon'
import Tooltip from 'scoreboard/components/tooltip'
import gradients from './gradients'
import Thresholds from './thresholds'
import ThresholdField from './threshold-field'
import $ from 'jquery'
import CovertButtonTooltips from 'scoreboard/components/covert-button-tooltips'

const segmentValues = [2,3,4,5,6,7,8,9,10,11]

function appendNewEl(el, props) {
	$(el).append($('<div>', props))
}

const GradientPalette = Backbone.View.extend({

	events: {
		'click .change-gradient': '_changeGradientId'
	},

	initialize: function(options) {
		this.reversed = options.reversed
	},

	render: function() {
		_.each(gradients.all(), (data, gradientId) => {
			let linearGradient = data.continuous(this.reversed).cssLinearGradient
			appendNewEl(this.$el, {
				class: 'change-gradient',
				css: { 'background': `linear-gradient(90deg, ${linearGradient})`},
				'data-gradient-id': gradientId
			})
		})
		return this
	},

	_changeGradientId: function(e) {
		let gradientId = this.$(e.target).data('gradient-id')
		this.trigger('change', gradientId)
	}
})


function buildSegmentedSvgDef(intervals) {
	let html = ''
	for (let i=0; i<=intervals.length-1; i++) {
		let data = intervals[i]
		let start = data.start*100
		let end = data.end*100
		html +=
			`<stop style="stop-color: ${data.color}" offset="${start}%"/>
			 <stop style="stop-color: ${data.color}" offset="${end}%"/>`
	}
	return html
}

const ThresholdPanel = Backbone.View.extend({

	events: {
		'change select[name=select-segments]' : '_changeSegments',
		'click .flip' : '_flipGradient',
		'click .threshold' : '_showThreshold',
		'click .gradients' : '_showGradients'
	},

	/**
	 * Displays the controls to manipulate a segmented or continuous gradient
	 * of colors for the report.  With this view you can change
	 * the color of the gradient, the number of segments (if segmented),
	 * and the thresholds within the gradient.
	 *
	 * @param {Boolean} [options.type='segement'] true if the gradient is
	 * continuous, false if segmented
	 * @param {Integer} [options.segments=3] number of segments the gradient has
	 * @param {Object} [options.thresholds] an has of the thresholds values for
	 * the gradient, where the key is the index that correspond to the placement
	 * of that threshold on the gradient.
	 * @param {String} [options.gradientId= the id of the gradient to start with
	 * @param {Boolean} [options.reversed=false] true if the color gradient
	 * is displayed starting from the minimum value to the maximum
	 * @param {Integer} options.min the minimum value to use if a lower bound
	 * threshold is not defined
	 * @param {Integer} options.max the maximum value to use if an upper bound
	 * threshold is not defined
	 * @param {Boolean} [options.isShapeWidget=false] used to determine specific
	 * logic for shape widgets, such as when defining lowest and highest
	 * conditional value thresholds
	 * @param {String} [options.dataType] the data type of the thresholds
	 * @param {String} [options.currencyCode] the currency code of the thresholds
	 * @param {String} [options.precision] the decimal precision of the
	 * thresholds
	 * @param {Boolean} [options.isGaugeWidget=false] if this is for a dashboards
	 * gauge widget
	 */
	initialize: function(options = {}) {
		this.options = options
		this.segments = this.options.segments || 3
		this.reversed = !!this.options.reversed
		this.continuous = this.options.type == 'continuous'
		this.type = this.options.type
		this.isShapeWidget = this.options?.isShapeWidget ?? false
		this.isGaugeWidget = this.options?.isGaugeWidget ?? false

		this.min = this.options.min
		this.max = this.options.max

		this.thresholdFields = []

		this.thresholds = new Thresholds(this.options)
		this.thresholds.resize(this._thresholdSize())

		this._setGradient(this.options.gradient || gradients.ids()[0])
	},

	render: function() {
		this.$el.html(template())
		this._renderComponents()
		this.covertButtonTooltips = new CovertButtonTooltips({ el: this.el })
		return this
	},

	_renderComponents: function() {
		this._renderSegments()
		this._renderCurrentGradient()
		this._renderThresholds()
	},

	_thresholdSize: function() {
		let thresholdSize = this.continuous ?
			this.segments : this.segments + 1
		// #57905
		// Remove the outermost thresholds for shape widgets
		return this.isShapeWidget ? thresholdSize - 2 : thresholdSize
	},

	_renderSegments: function() {
		let select = this.$('select[name=select-segments]')

		let options = _.map(segmentValues, segments => {
			let { intervals } = this._gradient.discrete(segments, this.reversed)
			let svgDefHtml = buildSegmentedSvgDef(intervals)
			let html =
				`<span class="segment-total">${segments}</span>
				<div class="segment-bar">
				 	<svg height="12">
						<defs>
							<linearGradient id="g${segments}">${svgDefHtml}</linearGradient>
						</defs>
						<rect width="100%" height="100%" rx="4" fill="url(#g${segments})"/>
					</svg>
				</div>`
			return {id:segments, value: segments, selected: this.segments == segments, html}
		})
		let format = (d) => { return d.html }

		selects(select, {
			options,
			render: {
				option: format,
				item: format
			}
		})
	},

	_renderCurrentGradient: function() {
		let linearGradient = this._gradient.continuous(this.reversed).cssLinearGradient
		let gradients = this.$('.gradients')
		gradients.empty().append(icon('edit'))
		appendNewEl(gradients, {
			class: 'gradient',
			css: { 'background': `linear-gradient(90deg, ${linearGradient})`}
		})
	},

	_showGradients: function(e) {
		this.gradientTooltip && this.gradientTooltip.remove()
		let attachTo = this.$(e.target)

		let content = new GradientPalette({ reversed: this.reversed })
		this.gradientTooltip = new Tooltip({
			attachTo,
			content,
			placement: 'below',
			arrow: 'center'
		}).render()

		content.on('change', (gradientId) => {
			this.gradientTooltip.hide()
			this._changeGradient(gradientId)
		})
	},

	_renderThresholds: function() {
		let gradientContainer = this.$('.colors-outside-thresholds').empty()
		if (this.continuous) {
			this._renderContinuousCssThresholdGradient(gradientContainer)
		} else {
			this._renderDiscreteCssThresholdGradient(gradientContainer)
		}

		let thresholdContainer = this.$('.thresholds-container')
		let thresholdSize = this._thresholdSize()

		thresholdContainer.toggleClass('is-shape-widget', this.isShapeWidget)

		for (let i=0; i<thresholdSize; i++) {
			if (!this.thresholdFields[i]) {
				let field = new ThresholdField({
					thresholds: this.thresholds,
					index: i,
					isShapeWidget: this.isShapeWidget,
					dataType: this.options.dataType,
					currencyCode: this.options.currencyCode,
					precision: this.options.precision,
					isGaugeWidget: this.isGaugeWidget
				}).render()

				//when a successful change is made, other invalid thresholds may
				//be valid, so revalidate them
				field.on('change', () => {
					_.each(this.thresholdFields, (field, index) => {
						if (index != i) {
							field.validate()
						}
					})

					this.trigger('thresholds', this.thresholds.values(false))
				})

				this.thresholdFields[i] = field
				field.$el.addClass('threshold')
				thresholdContainer.append(field.el)
			} else {
				this.thresholdFields[i].updatePlaceholder()
				// #59369 also update the marked field of the other thresholds
				// after adding more segments
				this.thresholdFields[i].updateMarkedField()
			}
		}

		//remove any extra thresholds no longer defined

		for (let i=thresholdSize; i<this.thresholdFields.length; i++) {
			_.invoke(this.thresholdFields[i], 'remove')
		}
		this.thresholdFields.splice(thresholdSize, this.thresholdFields.length-thresholdSize)
	},

	/**
	 * Renders a vertical continuous gradient that shows next to the threshold fields
	 */
	_renderContinuousCssThresholdGradient: function(container) {
		let gradient = this._gradient.continuous(this.reversed).cssLinearGradient
		let background = `background: linear-gradient(180deg, ${gradient}); width:20px; height: 100px`
		container.append(`<div style="${background}"></div>`)
	},

	/**
	 * Renders a gradient represented by colored circles that show next to the threshold fields
	 */
	_renderDiscreteCssThresholdGradient: function(container) {
		let { intervals } = this._gradient.discrete(this.segments, this.reversed)
		// drop the first color since when there's a tie we use the color
		// on the right (https://github.com/SpiderStrategies/Scoreboard/issues/20258#issuecomment-1115120330)
		let dashConnectorColors = _.drop(_.map(intervals, 'color'))
		// the last color is repeated for the last segment
		dashConnectorColors.push(_.last(intervals).color)
		let dashConnectorColorIndex = 0
		_.each(intervals, (segment, i) => {
			let color = segment.color
			let colorFormatted = color.formatHex()
			// Use a light color if value is white
			let adjustedColor = colorFormatted === '#ffffff'
				? 'rgba(0,0,0,.1)' : colorFormatted

			if (i === 0 && !this.isShapeWidget) {
				this.renderDashConnector(container, adjustedColor) // FIXME - use correct color
			}

			appendNewEl(container, {
				class: 'color',
				css: {
					'background': `rgba(${color.r}, ${color.g}, ${color.b}, .8)`,
					'border-color': adjustedColor
				}
			})
			// #57905
			// Skip setting the connector for the last index if it
			// is a shape widget
			if (i === intervals.length - 1 && this.isShapeWidget) {
				return
			}
			adjustedColor = dashConnectorColors[dashConnectorColorIndex++]
			this.renderDashConnector(container, adjustedColor)
		})
	},

	renderDashConnector: function(container, color) {
		appendNewEl(container, {
			class: 'dash-connector',
			css: {
				'border-top-color': color
			}
		})
	},

	_changeSegments: function(e) {
		let segementOption = this.$(e.target)
		this.segments = _.parseInt(segementOption.val())
		this.thresholds.resize(this._thresholdSize())
		this._renderComponents()
		this.trigger('segments', this.segments)
	},

	_changeGradient: function(gradientId) {
		this._setGradient(gradientId)
		this._renderComponents()
		this.trigger('gradient', gradientId)
	},

	_setGradient: function(gradientId) {
		this._gradient = gradients.byId(gradientId)
	},

	_flipGradient: function() {
		this.reversed = !this.reversed
		this._renderComponents()
		this.trigger('reversed', this.reversed)
	},

	remove: function() {
		_.invokeMap([this.thresholdFields, this.covertButtonTooltips], 'remove')
		Backbone.View.prototype.remove.apply(this, arguments)
	}
})

export default ThresholdPanel

ThresholdPanel.__filename__ = 'components/threshold-colors/threshold-panel.js'
